import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucidePlus,
  lucideChevronDown,
  lucideChevronUp,
  lucideRefreshCw,
} from '@ng-icons/lucide';

import { DynamicListComponent } from '@mm-aggregator/drf-dynamics';
import { UI, type UiBadgeVariant } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import { JournalService, type JournalStats } from '../../services/journal.service';
import { RoleAccessService } from '../../services/role-access.service';
import { ShellRoutesService } from '../../services/shell-routes.service';

/**
 * Custom Journal list page with expandable overview stats section
 * and the standard dynamic list of journal entries.
 */
@Component({
  selector: 'app-journal-list',
  standalone: true,
  imports: [CommonModule, RouterLink, DynamicListComponent, NgIcon, ...UI],
  templateUrl: './journal-list.component.html',
  viewProviders: [
    provideIcons({
      lucidePlus,
      lucideChevronDown,
      lucideChevronUp,
      lucideRefreshCw,
    }),
  ],
})
export class JournalListComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;

  private readonly journalService = inject(JournalService);
  private readonly router = inject(Router);
  readonly roleAccess = inject(RoleAccessService);
  readonly shellRoutes = inject(ShellRoutesService);

  readonly stats = signal<JournalStats | null>(null);
  readonly statsLoading = signal(false);
  readonly statsError = signal<string | null>(null);
  readonly overviewExpanded = signal(true);

  ngOnInit(): void {
    this.loadStats();
  }

  get listPath(): string {
    return this.entityConfig?.listPath ?? '';
  }

  get entityKey(): string {
    return this.entityConfig?.key ?? '';
  }

  loadStats(): void {
    this.statsLoading.set(true);
    this.statsError.set(null);
    this.journalService.getStats(30).subscribe({
      next: (data) => {
        this.stats.set(data);
        this.statsLoading.set(false);
      },
      error: (err) => {
        this.statsError.set(err?.message ?? 'Failed to load stats.');
        this.statsLoading.set(false);
      },
    });
  }

  toggleOverview(): void {
    this.overviewExpanded.set(!this.overviewExpanded());
  }

  onRowSelect(row: Record<string, unknown>): void {
    const id = row['id'] ?? row['pk'];
    if (id != null) {
      void this.router.navigate(this.shellRoutes.commands('manage', this.entityKey, String(id)));
    }
  }

  getEventTypeLabel(eventType: string): string {
    const labels: Record<string, string> = {
      INITIATION: 'Initiation',
      CONFIRMATION: 'Confirmation',
      REVERSAL: 'Reversal',
      REFUND: 'Refund',
      ADJUSTMENT: 'Adjustment',
      SETTLEMENT: 'Settlement',
    };
    return labels[eventType] ?? eventType;
  }

  getEventTypeVariant(eventType: string): UiBadgeVariant {
    const variants: Record<string, UiBadgeVariant> = {
      INITIATION: 'info',
      CONFIRMATION: 'success',
      REVERSAL: 'warning',
      REFUND: 'warning',
      ADJUSTMENT: 'brand',
      SETTLEMENT: 'brand',
    };
    return variants[eventType] ?? 'neutral';
  }

  getCurrencyCodes(): string[] {
    const s = this.stats();
    if (!s?.total_by_currency) return [];
    return Object.keys(s.total_by_currency);
  }
}
